import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router';
import { AmplifyAuthenticator, AmplifySignOut, AmplifyGreetings, AmplifySignIn, AmplifyAuthContainer, AmplifyToast } from '@aws-amplify/ui-react';
import { onAuthUIStateChange, AuthState, Translations } from '@aws-amplify/ui-components';
import { Amplify, I18n, Hub } from 'aws-amplify';
import { ThemeProvider, createMuiTheme } from '@material-ui/core/styles';
import { CssBaseline } from '@material-ui/core';
import { connect } from 'react-redux';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { TourProvider } from '@reactour/tour';
import TopBar from './UI/Menu/TopBar';
import RenderLogoByHostname from './UI/logos/RenderLogoByHostname.js';
import Router from './Router.js';
import SignerComponent from './Signer';
import BioadminComponent from './Bioadmin';
import LoadingCard from './UI/LoadingCard';
import { getConfig, saveSignerUser } from '../actions';
import { config, signerConfig, bioadminConfig } from '../constants';
import { CONSTANTS } from '../constants/constants';
import '../Analytics/Analytics';

const titles = process.env.REACT_APP_URUK_FRONT_TITLES.split(',');
const urls = process.env.REACT_APP_URUK_FRONT_URL.split(',');

const AMPLIFY_DICT_ES = {
  [Translations.SIGN_IN_HEADER_TEXT]: 'Iniciar sesión en su cuenta',
  [Translations.SIGN_IN_ACTION]: 'INICIAR SESIÓN',
  [Translations.USERNAME_LABEL]: 'Nombre de usuario *',
  [Translations.USERNAME_PLACEHOLDER]: 'Introduzca su nombre de usuario',
  [Translations.PASSWORD_LABEL]: 'Contraseña *',
  [Translations.PASSWORD_PLACEHOLDER]: 'Introduzca su contraseña',
  [Translations.FORGOT_PASSWORD_TEXT]: '¿Olvidó su contraseña?',
  [Translations.RESET_PASSWORD_TEXT]: 'Reestablecer contraseña',
  [Translations.RESET_YOUR_PASSWORD]: 'Reestablezca su contraseña',
  [Translations.BACK_TO_SIGN_IN]: 'Volver a iniciar sesión',
  [Translations.SEND_CODE]: 'Enviar código',
  [Translations.CONFIRM_SMS_CODE]: 'Confirmar código SMS',
  [Translations.CODE_LABEL]: 'Código de verificación',
  [Translations.CODE_PLACEHOLDER]: 'Introduzca el código',
  [Translations.CONFIRM]: 'CONFIRMAR',
  [Translations.SIGN_IN_WITH_AWS]: 'Iniciar sesión con cuenta corporativa',
  LOGIN_TRY_AGAIN: 'Ha ocurrido un error al iniciar sesión. Por favor, inténtelo de nuevo.',
  SSO_SIGN_UP_USER_NOT_FOUND: 'Usuario no encontrado. Por favor, contacte con el administrador IT de su compañía.',
  SIGN_IN_PASSWORD_EXPIRED: 'La contraseña ha expirado. Por favor, reestablezca su contraseña.'
};

const AMPLIFY_DICT_EN = {
  LOGIN_TRY_AGAIN: 'There was an error logging in. Please try again.',
  [Translations.SIGN_IN_WITH_AWS]: 'Sign in with corporate account',
  SSO_SIGN_UP_USER_NOT_FOUND: 'User not found. Please contact your company IT administrator.',
  SIGN_IN_PASSWORD_EXPIRED: 'The password has expired. Please reset your password.'
};

I18n.putVocabulariesForLanguage('es', AMPLIFY_DICT_ES);
I18n.putVocabulariesForLanguage('es-ES', AMPLIFY_DICT_ES);
I18n.putVocabulariesForLanguage('en', AMPLIFY_DICT_EN);
I18n.putVocabulariesForLanguage('en-GB', AMPLIFY_DICT_EN);

const App = (props) => {
  const { config: configApp } = props;
  const [authError, setAuthError] = useState(null);
  const [authState, setAuthState] = useState();
  const [loading, setLoading] = useState(false);
  const [isSigner, setIsSigner] = useState(false);
  const [isBioadmin, setIsBioadmin] = useState(false);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const history = useHistory();
  const setAmplifyConfig = () => {
    if (history.location.pathname.startsWith('/signer')) {
      Amplify.configure(signerConfig);
      setIsSigner(true);
    } else if (history.location.pathname.startsWith('/bioadmin')) {
      Amplify.configure(bioadminConfig);
      setIsBioadmin(true);
    } else {
      Amplify.configure(config);
      setIsSigner(false);
    }
  };

  const { t } = useTranslation();

  const handleSignInError = (data) => {
    if (data && data.message.includes('SignInPasswordExpired')) {
      setAuthError(I18n.get('SIGN_IN_PASSWORD_EXPIRED', null));
    } else if (data && data.code) {
      const message = data.message || '';
      switch (data.code) {
        case CONSTANTS.EXCEPTIONS.UNEXPECTED_LAMBDA_EXCEPTION.CODE:
          if (message.toLowerCase().includes(CONSTANTS.EXCEPTIONS.UNEXPECTED_LAMBDA_EXCEPTION.ERROR_SOCKET_TIMEOUT)) {
            data.message = I18n.get('LOGIN_TRY_AGAIN', null) || AMPLIFY_DICT_EN.LOGIN_TRY_AGAIN;
          }
          break;
        case CONSTANTS.EXCEPTIONS.USER_LAMBDA_VALIDATION_EXCEPTION.CODE: {
          data.message = message.replaceAll(CONSTANTS.EXCEPTIONS.USER_LAMBDA_VALIDATION_EXCEPTION.PRETOKENGENERATION_FAILED, '');
        }
      }
      setAuthError(data.message);
    } else if (data.message.includes('SSOSignUpUserNotFound')) {
      setAuthError(I18n.get('SSO_SIGN_UP_USER_NOT_FOUND', null));
    }
  };

  const initializeApp = async (user) => {
    try {
      const config = await props.getConfig(user);
      await i18next.loadNamespaces(`${config.id}-common`);
      i18next.setDefaultNamespace(`${config.id}-common`);
    } catch (err) {
      console.error('Error when getting Application table: ', err);
    } finally {
      setLoading(false);
    }
  };
  const setLocalStorageForSigner = (authData) => {
    const baseKey = `CognitoIdentityServiceProvider.${process.env.REACT_APP_USER_POOLCLIENT_SIGNER}.${authData.username}.`;

    localStorage.setItem(`${baseKey}idToken`, authData.signInUserSession.idToken.jwtToken);
    localStorage.setItem(`${baseKey}refreshToken`, authData.signInUserSession.refreshToken.token);
    localStorage.setItem(`${baseKey}accessToken`, authData.signInUserSession.accessToken.jwtToken);
    localStorage.setItem(`CognitoIdentityServiceProvider.${process.env.REACT_APP_USER_POOLCLIENT_SIGNER}.LastAuthUser`, authData.username);
  };

  useEffect(() => {
    Hub.listen('auth', (data) => {
      if (data && data.payload && data.payload.event === CONSTANTS.AMPLIFY_EVENTS_TYPE.SIGN_IN_FAILURE) {
        handleSignInError(data.payload.data);
      }
    });

    if (history.location.pathname.startsWith('/signer')) {
      return onAuthUIStateChange((nextAuthState, authData) => {
        if (nextAuthState === AuthState.SignedIn) {
          setIsLoggedIn(true);
          props.saveSignerUser(authData);
        } else if (nextAuthState === AuthState.SignIn) {
          setIsLoggedIn(false);
        }
        setAuthState(nextAuthState);
      });
    } else {
      return onAuthUIStateChange((nextAuthState, authData) => {
        if (nextAuthState === AuthState.SignedIn) {
          authData.username = authData.attributes.preferred_username || authData.username;
          setLoading(true);
          setIsLoggedIn(true);
          setLocalStorageForSigner(authData);
          initializeApp(authData);
        } else if (nextAuthState === AuthState.SignIn) {
          setIsLoggedIn(false);
        }

        setAuthState(nextAuthState);
      });
    }
  }, []);
  useEffect(() => {
    setAmplifyConfig();
  }, []);

  useEffect(() => {
    const hostname = window.location.hostname;
    if (hostname.includes(urls[0])) {
      document.title = titles[0];
    } else if (hostname.includes(urls[2])) {
      document.title = titles[2];
    } else {
      document.title = titles[1];
    }
  }, []);

  const renderApp = () => {
    const isEmpty = (obj) => Object.keys(obj).length === 0;
    if (loading) return <LoadingCard open loading message={t('loadingCard:loading')} />;

    if (isLoggedIn) {
      if (isBioadmin) {
        return <BioadminComponent />;
      }
      if (!isEmpty(configApp)) {
        return (
          <CheckEnableTour enableTour={configApp.enableTour}>
            <ThemeProvider
              theme={(theme) => {
                if (!configApp.theme) return theme;
                const customThemePalette = configApp.theme.palette;
                const customTheme = createMuiTheme({
                  ...theme,
                  palette: {
                    ...theme.palette,
                    ...customThemePalette
                  }
                });
                return customTheme;
              }}
            >
              <CssBaseline>
                <TopBar setLoading={setLoading} />
                <Router config={configApp} forceRefresh />
              </CssBaseline>
            </ThemeProvider>
          </CheckEnableTour>);
      } else {
        return <SignerComponent />;
      }
    } else {
      return (
        <div style={{
          display: 'flex',
          width: '100%',
          justifyContent: 'center',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100vh'

        }}>
          <div>
            {authState !== AuthState.SignedIn && authState !== AuthState.VerifyContact && <RenderLogoByHostname /> }
          </div>
          <AmplifyAuthContainer style={{
            display: 'flex',
            justifyContent: 'center',
            maxHeight: '450px',
            alignItems: 'center'
          }}>
            {authError && <AmplifyToast message={authError} handleClose={() => setAuthError(null)} />}
            <AmplifyAuthenticator hide={[AmplifySignOut, AmplifyGreetings]} initialAuthState='signin' hideToast>
              <AmplifySignIn slot='sign-in' hideSignUp={!isSigner} />
            </AmplifyAuthenticator>
          </AmplifyAuthContainer>
        </div>
      );
    }
  };

  return (
    <CssBaseline>
      {renderApp()}
    </CssBaseline>
  );
};

const CheckEnableTour = ({ children, enableTour }) => (enableTour
  ? <TourProvider >
    {children}
  </TourProvider>
  : children);

const mapStateToProps = (state) => {
  return {
    config: state.config
  };
};

const mapDispatchToProps = (dispatch) => {
  return {
    getConfig: (data) => dispatch(getConfig(data)),
    saveSignerUser: (user) => dispatch(saveSignerUser(user))
  };
};
export default connect(mapStateToProps, mapDispatchToProps)(App);
